import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    Card,
    Form
} from 'react-bootstrap';
import axios from "axios";
import {
    faSave,
    faRedo,

} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleLeft } from "@fortawesome/free-regular-svg-icons";
import {
    matchPath,
    useLocation,
    useNavigate,
    useParams
} from 'react-router-dom';
import { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { AppDispatch, RootState } from './store';
import { useBackNavigation } from '../../../hooks/BackNavigation';
import { AESDecrypt } from '../../../encrypt-util';
import { addTemplate, getTemplateList } from '../../../template';
import UnsavedChangesModal from '../../../components/common/UnsavedChangesModal';

const FormSubmission = () => {
    const { master_trucker_id } = useParams();
    const masterTruckerId = AESDecrypt(master_trucker_id);

    const [loading, setLoading] = useState(false);
    const [schemaOptions, setSchemaOptions] = useState<string[]>([]);
    const [fileFormatOptions, setFileFormatOptions] = useState<string[]>([]);
    const profileDetails = useSelector(
        (state: RootState) => state.userProfile.profile
    );

    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const navigate = useNavigate();
    const onBack = useBackNavigation();

    const [showUnsavedModal, setShowUnsavedModal] = useState(false);

    const resetForm = () => {
        setLoading(false);
        reset();
    };

    const matchPathInstruction = () =>
        matchPath(
            {
                path: "/templateUpload/add",
                caseSensitive: true,
                end: true,
            },
            location.pathname
        );

    const _onCloseForm = () => {
        setLoading(false);
        navigate('/templateUpload');
    };

    type FormValues = {
        templateName: string;
        schemaName: string;
        fileFormat: string;
    };

    const validationSchema = Yup.object().shape({
        templateName: Yup.string().required('Template Name is required'),
        schemaName: Yup.string().required('Schema Name is required'),
        fileFormat: Yup.string().required('File Format is required')
    });

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        formState: { errors, isDirty },
        watch,
        clearErrors
    } = useForm<FormValues>({
        resolver: yupResolver(validationSchema),
        mode: 'onChange'
    });

    const onSubmit = handleSubmit(async (payload: FormValues) => {
        try {
            setLoading(true);
            const response = await dispatch(addTemplate(payload)).unwrap();
    
            if (response?.status === 200) {
                toast.success('Template submitted successfully!');
                reset();
                navigate('/templateUpload'); 
                  dispatch(getTemplateList())
            } else {
                toast.error('Something went wrong!');
            }
        } catch (err: any) {
            toast.error(err?.message || 'Submission failed!');
        } finally {
            setLoading(false);
        }
    });

    useEffect(() => {
      
        axios.get(
            `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguageWithoutSession?language=EN&codeType=FILE_EXTENSION&linerId=1&countryCode=US`
        
        )
            .then((response: any) => {
                const options = Array.isArray(response?.data)
                    ? response.data.map((item: any) => item?.codeDesc).filter(Boolean)
                    : [];
                setFileFormatOptions(options);
            })
            .catch(() => setFileFormatOptions([]));

        // Load Schema options for templates from mstCode (codeType=TEMPLATE_SCHEMA)
        axios.get(
            `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguageWithoutSession?language=EN&codeType=TEMPLATE_SCHEMA&linerId=1&countryCode=US`

        )
            .then((response: any) => {
                const options = Array.isArray(response?.data)
                    ? response.data.map((item: any) => item?.codeDesc).filter(Boolean)
                    : [];
                setSchemaOptions(options);
            })
            .catch(() => setSchemaOptions([]));
    }, []);

    const handleBack = () => {
        if (isDirty) {
            setShowUnsavedModal(true);
        } else {
            navigate(-1);
        }
    };

    const handleStayOnPage = () => {
        setShowUnsavedModal(false);
    };

    const handleLeavePage = () => {
        setShowUnsavedModal(false);
        navigate(-1);
    };

    return (
        <Card style={{ height: '100%', overflow: 'auto' }}>
            <Card.Header className='border-bottom d-flex justify-content-between align-items-center'>
                <FontAwesomeIcon
                    icon={faArrowAltCircleLeft}
                    
                    className='text-warning cursor-pointer fa-lg mb-1'
                    onClick={handleBack}
                />
                <div className='row col-12'>
                    <div className='col-lg-4'>
                        <h2 className='fs-0 text-primary'>New Template</h2>
                    </div>
                </div>
            </Card.Header>

            <Card.Body>
                <div className='container'>
                    <Form onSubmit={onSubmit} autoComplete='off'>
                        <div className='row'>
                            <div className='col-lg-12'>
                                <div className='row'>

                                    {/* Template Name */}
                                    <div className='col-lg-6'>
                                        <Form.Group controlId='template.name'>
                                            <span className='mandatory'>*</span>
                                            <Form.Label>Template Name</Form.Label>
                                            <Form.Control
                                                size='sm'
                                                {...register('templateName')}
                                                className={`fs--1 form-control ${errors.templateName ? 'is-invalid' : ''}`}
                                                type='text'
                                                onChange={(e) => {
                                                    setValue('templateName', e.target.value, { shouldValidate: true });
                                                    clearErrors("templateName");
                                                }}
                                                value={watch('templateName')}
                                            />
                                            <div className='invalid-feedback'>
                                                {errors.templateName?.message}
                                            </div>
                                        </Form.Group>
                                    </div>

                                    {/* Schema Name */}
                                    <div className='col-lg-6'>
                                        <Form.Group controlId='template.schemaName'>
                                            <span className='mandatory'>*</span>
                                            <Form.Label>Schema Name</Form.Label>
                                            <Form.Select
                                                {...register("schemaName")}
                                                onChange={(e) => {
                                                    setValue("schemaName", e.target.value, { shouldValidate: true });
                                                    clearErrors("schemaName");
                                                }}
                                                value={watch('schemaName')}
                                                className={`fs--1 ${errors.schemaName ? "is-invalid" : ""}`}
                                            >
                                                <option value="">-- Select Schema --</option>
                                             
                                                   { schemaOptions.map((opt) => (
                                                        <option key={opt} value={opt}>{opt}</option>
                                                    ))}
                                                
                                            </Form.Select>
                                            <div className='invalid-feedback'>
                                                {errors.schemaName?.message}
                                            </div>
                                        </Form.Group>
                                    </div>

                                    {/* File Format */}
                                    <div className='col-lg-6'>
                                        <Form.Group controlId='template.fileFormat'>
                                            <span className='mandatory'>*</span>
                                            <Form.Label>File Format</Form.Label>
                                            <Form.Select
                                                {...register("fileFormat")}
                                                onChange={(e) => {
                                                    setValue("fileFormat", e.target.value, { shouldValidate: true });
                                                    clearErrors("fileFormat");
                                                }}
                                                value={watch('fileFormat')}
                                                className={`fs--1 ${errors.fileFormat ? "is-invalid" : ""}`}
                                            >
                                                <option value="">-- Select Format --</option>
                                                {
                                                    fileFormatOptions.map((opt) => (
                                                        <option key={opt} value={opt}>{opt}</option>
                                                    ))
                                                }
                                            </Form.Select>
                                            <div className='invalid-feedback'>
                                                {errors.fileFormat?.message}
                                            </div>
                                        </Form.Group>
                                    </div>

                                    {/* Buttons */}
                                    <div className='col-lg-8 offset-lg-2 mt-3 d-flex justify-content-center align-items-center'>
                                        <Button
                                            variant='falcon-primary me-2'
                                            size='sm'
                                            type='submit'
                                            disabled={loading}
                                        >
                                            <FontAwesomeIcon icon={faSave} className='me-2' />
                                            {t('resource_1:submit')}
                                        </Button>

                                        <Button
                                            variant='falcon-danger'
                                            onClick={resetForm}
                                            size='sm'
                                        >
                                            <FontAwesomeIcon icon={faRedo} className='me-2' />
                                            {t('resource_1:reset')}
                                        </Button>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </Form>
                </div>
            </Card.Body>

            <UnsavedChangesModal
                show={showUnsavedModal}
                onHide={handleStayOnPage}
                onConfirm={handleLeavePage}
            />
        </Card>
    );
};

export default FormSubmission;
